import React, { useState, useEffect, useRef, ReactNode } from "react";
import { GoChevronDown } from "react-icons/go";
import classNames from "classnames";
import { FormikErrors, FormikTouched } from "formik";
import { twMerge } from "tailwind-merge";
import { NavOption, OrderType, OrderTypeText } from "../../types";
import { RiEBike2Line } from "react-icons/ri";
import { MdOutlineShoppingBag } from "react-icons/md";
import Skeleton from "../common/Skeleton";

interface DropdownProps<T> {
  id: string;
  label?: string;
  placeholder?: string;
  options?: NavOption[];
  value?: string;
  className?: string;
  isFetching?: boolean;
  errors?: FormikErrors<T>;
  touched?: FormikTouched<T>;
  setFieldValue: (field: string, value: string) => void;
  setFieldTouched?: (field: string, touched: boolean) => void;
}

const Dropdown: React.FC<DropdownProps<any>> = ({
  id,
  placeholder,
  options = [],
  value,
  label,
  className,
  isFetching = false,
  errors = {},
  touched = {},
  setFieldValue,
  setFieldTouched,
}) => {
  // hooks
  const [isOpen, setIsOpen] = useState(false);
  const divEl = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (event: MouseEvent) => {
      if (!divEl.current) return;
      if (!divEl.current.contains(event.target as Node)) setIsOpen(false);
    };

    document.addEventListener("click", handler, false);

    return () => {
      document.removeEventListener("click", handler);
    };
  }, []);

  useEffect(() => {
    if (
      value &&
      options?.length &&
      !options.some((optionElem) => optionElem.label === value)
    )
      setFieldValue(id, options[0].label);
  }, [options, id, value, setFieldValue]);

  // classes
  const dropdownClasses = classNames(
    className,
    "relative cursor-pointer rounded"
  );
  const inputContainerClasses = twMerge(
    classNames(
      "flex items-center bg-white rounded p-2.5 space-evenly",
      {
        "border border-red-700": errors[id] && touched[id],
      },
      className
    )
  );
  const errorMessageClasses = classNames("text-sm text-red-700");

  // event handlers
  const handleClick = () => {
    setIsOpen((currentIsOpen) => !currentIsOpen);
  };

  const handleOptionClick = (selectedOption: string) => {
    setIsOpen(false);
    setFieldTouched && setFieldTouched(id, true);
    setFieldValue(id, selectedOption);
  };

  // renders
  const renderedOptions = options.map((optionElem) => {
    const typedOptionLabel = optionElem.label as OrderType;
    const Icon = optionElem.icon;
    return (
      <div
        key={typedOptionLabel}
        className="flex gap-2 items-center hover:bg-gray-200 rounded cursor-pointer p-1"
        onClick={() => handleOptionClick(typedOptionLabel)}
      >
        <Icon />
        <div>{OrderTypeText[typedOptionLabel]}</div>
      </div>
    );
  });

  const getIconByValue = (value: string) => {
    const classes = "h-6 w-6";
    if (value === OrderType.DELIVERY)
      return <RiEBike2Line className={classes} />;
    else return <MdOutlineShoppingBag className={classes} />;
  };

  return (
    <div ref={divEl} className={dropdownClasses}>
      <div className={inputContainerClasses}>
        {label && <div className="font-semibold"> {label} </div>}
        <div
          className="w-full flex items-center gap-2 px-4 py-2"
          onClick={handleClick}
        >
          {value && getIconByValue(value)}
          <input
            value={value ? OrderTypeText[value as OrderType] : ""}
            placeholder={placeholder}
            className="w-full bg-transparent cursor-pointer"
            readOnly
          />
          <GoChevronDown className="text-lg" />
        </div>
      </div>

      {isOpen && (
        <div className="absolute top-full w-full border bg-white z-30 p-1 my-1 max-h-80 overflow-y-auto">
          {isFetching ? (
            <Skeleton className="h-10 w-full" times={3} />
          ) : (
            renderedOptions
          )}
        </div>
      )}

      <div className="px-2">
        {errors[id] && touched[id] && (
          <div className={errorMessageClasses}> {errors[id] as ReactNode} </div>
        )}
      </div>
    </div>
  );
};

export default Dropdown;
